import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  FormHelperText,
  Select,
  Divider,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Center,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiImage } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp, collection, getDocs, addDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

export interface GiftCardTemplate {
  id?: string;
  name: string;
  theme: string;
  imageUrl?: string;
  denominations: number[]; // In cents
  isActive: boolean;
  isSeasonal?: boolean;
  seasonalStartDate?: any;
  seasonalEndDate?: any;
  description?: string;
  createdAt?: any;
  updatedAt?: any;
}

export interface AppSettings {
  id: string;
  giftCardTemplates?: GiftCardTemplate[];
  createdAt: any;
  updatedAt: any;
  updatedBy: string;
}

const DEFAULT_TEMPLATE: Omit<GiftCardTemplate, 'id'> = {
  name: '',
  theme: 'general',
  imageUrl: '',
  denominations: [2500, 5000, 10000, 25000, 50000], // $25, $50, $100, $250, $500 in cents
  isActive: true,
  isSeasonal: false,
  description: '',
};

const THEME_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'birthday', label: 'Birthday' },
  { value: 'anniversary', label: 'Anniversary' },
  { value: 'thank-you', label: 'Thank You' },
  { value: 'corporate', label: 'Corporate' },
];

const GiftCardTemplatesComponent: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<GiftCardTemplate[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<GiftCardTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<Omit<GiftCardTemplate, 'id'>>(DEFAULT_TEMPLATE);
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      // Load from appSettings > default > giftCardTemplates subcollection
      const appSettingsRef = doc(db, 'appSettings', 'default');
      const templatesRef = collection(appSettingsRef, 'giftCardTemplates');
      const templatesSnapshot = await getDocs(templatesRef);
      
      const loadedTemplates: GiftCardTemplate[] = [];
      templatesSnapshot.forEach((doc) => {
        loadedTemplates.push({ id: doc.id, ...doc.data() } as GiftCardTemplate);
      });
      
      // Sort by name
      loadedTemplates.sort((a, b) => a.name.localeCompare(b.name));
      setTemplates(loadedTemplates);
      console.log(`✅ Loaded ${loadedTemplates.length} gift card templates from appSettings/default/giftCardTemplates`);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Error',
        description: 'Failed to load gift card templates',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm(DEFAULT_TEMPLATE);
    onModalOpen();
  };

  const handleEditTemplate = (template: GiftCardTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      theme: template.theme,
      imageUrl: template.imageUrl || '',
      denominations: [...template.denominations],
      isActive: template.isActive,
      isSeasonal: template.isSeasonal || false,
      seasonalStartDate: template.seasonalStartDate,
      seasonalEndDate: template.seasonalEndDate,
      description: template.description || '',
    });
    onModalOpen();
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      // Use appSettings > default > giftCardTemplates subcollection
      const appSettingsRef = doc(db, 'appSettings', 'default');
      const templatesRef = collection(appSettingsRef, 'giftCardTemplates');
      await deleteDoc(doc(templatesRef, templateId));
      setTemplates(templates.filter(t => t.id !== templateId));
      toast({
        title: 'Success',
        description: 'Template deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSaveTemplate = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save templates',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate
    if (!templateForm.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Template name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (templateForm.denominations.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'At least one denomination is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSaving(true);

      // Only include seasonal dates if isSeasonal is true
      const templateData: Omit<GiftCardTemplate, 'id'> = {
        ...templateForm,
        // Conditionally include seasonal dates only if isSeasonal is true
        seasonalStartDate: templateForm.isSeasonal ? templateForm.seasonalStartDate : undefined,
        seasonalEndDate: templateForm.isSeasonal ? templateForm.seasonalEndDate : undefined,
        updatedAt: Timestamp.now(),
      };

      // Use appSettings > default > giftCardTemplates subcollection
      const appSettingsRef = doc(db, 'appSettings', 'default');
      const templatesRef = collection(appSettingsRef, 'giftCardTemplates');

      if (editingTemplate?.id) {
        // Update existing
        await updateDoc(doc(templatesRef, editingTemplate.id), templateData);
        setTemplates(templates.map(t => 
          t.id === editingTemplate.id 
            ? { ...t, ...templateData }
            : t
        ));
      } else {
        // Create new
        const newTemplate = {
          ...templateData,
          createdAt: Timestamp.now(),
        };
        const docRef = await addDoc(templatesRef, newTemplate);
        setTemplates([...templates, { id: docRef.id, ...newTemplate }]);
      }

      onModalClose();
      toast({
        title: 'Success',
        description: editingTemplate ? 'Template updated successfully' : 'Template created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: 'Failed to save template',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field: keyof Omit<GiftCardTemplate, 'id'>, value: any) => {
    setTemplateForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddDenomination = () => {
    const newAmount = prompt('Enter denomination amount in dollars (e.g., 25 for $25):');
    if (newAmount) {
      const amountInCents = Math.round(parseFloat(newAmount) * 100);
      if (!isNaN(amountInCents) && amountInCents > 0) {
        if (!templateForm.denominations.includes(amountInCents)) {
          handleFormChange('denominations', [...templateForm.denominations, amountInCents].sort((a, b) => a - b));
        } else {
          toast({
            title: 'Duplicate',
            description: 'This denomination already exists',
            status: 'warning',
            duration: 2000,
            isClosable: true,
          });
        }
      }
    }
  };

  const handleRemoveDenomination = (amount: number) => {
    handleFormChange('denominations', templateForm.denominations.filter(d => d !== amount));
  };

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box>
      <Box mb={6}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          mb={4}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
        >
          Gift Card Templates
        </Heading>
        
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              Manage gift card templates that customers can purchase. Each template defines available denominations and visual design.
            </Text>
          </Box>
        </Alert>
      </Box>

      <HStack mb={6} justify="space-between">
        <Text fontSize="sm" color="gray.600">
          {templates.length} template{templates.length !== 1 ? 's' : ''} configured
        </Text>
        <Button
          leftIcon={React.createElement(FiPlus as React.ElementType)}
          colorScheme="blue"
          onClick={handleAddTemplate}
        >
          Add Template
        </Button>
      </HStack>

      {templates.length === 0 ? (
        <Card>
          <CardBody>
            <Center py={8}>
              <VStack spacing={4}>
                <Text color="gray.500">No gift card templates configured</Text>
                <Button
                  leftIcon={React.createElement(FiPlus as React.ElementType)}
                  colorScheme="blue"
                  onClick={handleAddTemplate}
                >
                  Create First Template
                </Button>
              </VStack>
            </Center>
          </CardBody>
        </Card>
      ) : (
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Theme</Th>
                <Th>Denominations</Th>
                <Th>Status</Th>
                <Th>Seasonal</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {templates.map((template) => (
                <Tr key={template.id}>
                  <Td fontWeight="bold">{template.name}</Td>
                  <Td>
                    <Badge colorScheme="purple">{template.theme}</Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      {template.denominations.map(amount => (
                        <Badge key={amount} size="sm" colorScheme="green">
                          ${(amount / 100).toFixed(0)}
                        </Badge>
                      ))}
                    </HStack>
                  </Td>
                  <Td>
                    <Badge colorScheme={template.isActive ? 'green' : 'gray'}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    {template.isSeasonal ? (
                      <Badge colorScheme="orange">Seasonal</Badge>
                    ) : (
                      <Text fontSize="sm" color="gray.500">No</Text>
                    )}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="Edit Template">
                        <IconButton
                          size="sm"
                          icon={React.createElement(FiEdit2 as React.ElementType)}
                          aria-label="Edit template"
                          variant="ghost"
                          onClick={() => handleEditTemplate(template)}
                        />
                      </Tooltip>
                      <Tooltip label="Delete Template">
                        <IconButton
                          size="sm"
                          icon={React.createElement(FiTrash2 as React.ElementType)}
                          aria-label="Delete template"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => template.id && handleDeleteTemplate(template.id)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingTemplate ? 'Edit Gift Card Template' : 'Create Gift Card Template'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Template Name</FormLabel>
                <Input
                  value={templateForm.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="Holiday Gift Card"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Theme</FormLabel>
                <Select
                  value={templateForm.theme}
                  onChange={(e) => handleFormChange('theme', e.target.value)}
                >
                  {THEME_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Image URL</FormLabel>
                <Input
                  value={templateForm.imageUrl}
                  onChange={(e) => handleFormChange('imageUrl', e.target.value)}
                  placeholder="https://example.com/gift-card-image.png"
                />
                <FormHelperText>URL to the gift card template image</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={templateForm.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder="Description of this gift card template..."
                  rows={3}
                />
              </FormControl>

              <Divider />

              <FormControl>
                <FormLabel>Denominations (in dollars)</FormLabel>
                <VStack spacing={2} align="stretch">
                  <HStack spacing={2} flexWrap="wrap">
                    {templateForm.denominations.map(amount => (
                      <Badge
                        key={amount}
                        size="lg"
                        colorScheme="green"
                        p={2}
                        borderRadius="md"
                      >
                        ${(amount / 100).toFixed(0)}
                        <IconButton
                          size="xs"
                          icon={React.createElement(FiTrash2 as React.ElementType)}
                          aria-label="Remove"
                          variant="ghost"
                          ml={2}
                          onClick={() => handleRemoveDenomination(amount)}
                        />
                      </Badge>
                    ))}
                  </HStack>
                  <Button
                    size="sm"
                    leftIcon={React.createElement(FiPlus as React.ElementType)}
                    onClick={handleAddDenomination}
                    variant="outline"
                  >
                    Add Denomination
                  </Button>
                </VStack>
                <FormHelperText>
                  Available dollar amounts customers can purchase (e.g., $25, $50, $100)
                </FormHelperText>
              </FormControl>

              <Divider />

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0} flex="1">
                  Active
                </FormLabel>
                <Switch
                  isChecked={templateForm.isActive}
                  onChange={(e) => handleFormChange('isActive', e.target.checked)}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0} flex="1">
                  Seasonal Template
                </FormLabel>
                <Switch
                  isChecked={templateForm.isSeasonal}
                  onChange={(e) => handleFormChange('isSeasonal', e.target.checked)}
                />
              </FormControl>

              {templateForm.isSeasonal && (
                <>
                  <FormControl>
                    <FormLabel>Seasonal Start Date</FormLabel>
                    <Input
                      type="date"
                      value={templateForm.seasonalStartDate ? 
                        new Date(templateForm.seasonalStartDate.toDate ? templateForm.seasonalStartDate.toDate() : templateForm.seasonalStartDate).toISOString().split('T')[0] 
                        : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          handleFormChange('seasonalStartDate', Timestamp.fromDate(new Date(e.target.value)));
                        }
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Seasonal End Date</FormLabel>
                    <Input
                      type="date"
                      value={templateForm.seasonalEndDate ? 
                        new Date(templateForm.seasonalEndDate.toDate ? templateForm.seasonalEndDate.toDate() : templateForm.seasonalEndDate).toISOString().split('T')[0] 
                        : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          handleFormChange('seasonalEndDate', Timestamp.fromDate(new Date(e.target.value)));
                        }
                      }}
                    />
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveTemplate}
              isLoading={saving}
              loadingText="Saving"
            >
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default GiftCardTemplatesComponent;

